"use client";

import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { AlertCircle } from "lucide-react";

interface CustomTextareaProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
  helperText?: string;
}

export function CustomTextarea({
  label,
  error,
  helperText,
  className = "",
  value,
  onFocus,
  onBlur,
  rows = 4,
  ...props
}: CustomTextareaProps) {
  const [isFocused, setIsFocused] = useState(false);

  return (
    <div className="w-full">
      {label && (
        <label className="block text-xs font-semibold uppercase tracking-wider text-[#8E8E93] mb-1.5">
          {label}
        </label>
      )}

      <motion.div
        animate={error ? { x: [-8, 8, -8, 8, 0] } : {}}
        transition={{ duration: 0.35 }}
      >
        <textarea
          {...props}
          rows={rows}
          value={value}
          onFocus={(e) => {
            setIsFocused(true);
            if (onFocus) onFocus(e);
          }}
          onBlur={(e) => {
            setIsFocused(false);
            if (onBlur) onBlur(e);
          }}
          className={`w-full bg-[#1C1C1E] rounded-[10px] text-sm text-[#FAFAFA] placeholder-[#8E8E93] transition-all duration-200 outline-none px-4 py-3 border resize-y ${
            error
              ? "border-[#FF453A] shadow-[0_0_0_3px_rgba(255,69,58,0.15)]"
              : isFocused
              ? "border-[#FFD60A] shadow-[0_0_0_3px_rgba(255,214,10,0.15)]"
              : "border-[rgba(255,255,255,0.1)] hover:border-[rgba(255,255,255,0.2)]"
          } ${className}`}
        />
      </motion.div>

      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -5 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -5 }}
            className="mt-1.5 flex items-center gap-1.5 text-xs font-medium text-[#FF453A]"
          >
            <AlertCircle className="w-3.5 h-3.5 shrink-0" />
            <span>{error}</span>
          </motion.div>
        )}
      </AnimatePresence>

      {!error && helperText && (
        <p className="mt-1.5 text-xs text-[#8E8E93]">{helperText}</p>
      )}
    </div>
  );
}
